import { Loader2, X } from "lucide-react";
import PropTypes from "prop-types";

export const AcceptModal = ({
	isOpen,
	onClose,
	onAccept,
	formData,
	setFormData,
	formErrors,
	setFormErrors,
	loading,
	proposalNumber,
	totalValue,
	acceptanceConfig,
}) => {
	if (!isOpen) return null;
	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
			<button
				type="button"
				className="absolute inset-0 bg-black/80 backdrop-blur-sm w-full h-full border-none"
				onClick={onClose}
				aria-label="Fechar modal"
			></button>
			<div className="relative bg-[#181C25] border border-slate-700 rounded-2xl p-8 max-w-lg w-full shadow-2xl">
				<button
					type="button"
					onClick={onClose}
					className="absolute top-4 right-4 text-slate-500 hover:text-white"
				>
					<X size={20} />
				</button>
				<div className="mb-6">
					<h3 className="text-2xl font-bold text-white">
						Formalizar Contratacao
					</h3>
					<p className="text-slate-400 text-sm mt-1">
						Aprovando proposta <strong>{proposalNumber}</strong> -{" "}
						<strong>R$ {totalValue}</strong>.
					</p>
				</div>
				{formErrors.general && (
					<div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
						{formErrors.general}
					</div>
				)}
				<form
					onSubmit={(e) => {
						e.preventDefault();
						onAccept();
					}}
					className="space-y-4"
				>
					<div>
						<label
							htmlFor="approver_name"
							className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider"
						>
							Responsavel *
						</label>
						<input
							id="approver_name"
							required
							type="text"
							placeholder="Seu nome completo"
							value={formData.approver_name}
							onChange={(e) => {
								setFormData({ ...formData, approver_name: e.target.value });
								setFormErrors({});
							}}
							className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
						/>
						{formErrors.approver_name && (
							<p className="text-xs text-red-400 mt-1">
								{formErrors.approver_name}
							</p>
						)}
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label
								htmlFor="approver_role"
								className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider"
							>
								Cargo {acceptanceConfig.require_role && "*"}
							</label>
							<input
								id="approver_role"
								required={acceptanceConfig.require_role}
								type="text"
								placeholder="Ex: CEO"
								value={formData.approver_role}
								onChange={(e) =>
									setFormData({ ...formData, approver_role: e.target.value })
								}
								className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
							/>
						</div>
						<div>
							<label
								htmlFor="approver_phone"
								className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider"
							>
								Telefone {acceptanceConfig.require_phone && "*"}
							</label>
							<input
								id="approver_phone"
								required={acceptanceConfig.require_phone}
								type="text"
								placeholder="+55 (11) 9999-9999"
								value={formData.approver_phone}
								onChange={(e) =>
									setFormData({ ...formData, approver_phone: e.target.value })
								}
								className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
							/>
						</div>
					</div>
					<div>
						<label
							htmlFor="approver_email"
							className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider"
						>
							Email *
						</label>
						<input
							id="approver_email"
							required
							type="email"
							placeholder="seu.email@empresa.com"
							value={formData.approver_email}
							onChange={(e) => {
								setFormData({ ...formData, approver_email: e.target.value });
								setFormErrors({});
							}}
							className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
						/>
						{formErrors.approver_email && (
							<p className="text-xs text-red-400 mt-1">
								{formErrors.approver_email}
							</p>
						)}
					</div>

					{acceptanceConfig.legal_text && (
						<p className="text-[10px] text-slate-500 leading-relaxed bg-black/20 p-3 rounded-lg border border-white/5">
							{acceptanceConfig.legal_text}
						</p>
					)}

					<button
						type="submit"
						disabled={loading}
						className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-4 rounded-xl shadow-lg transition-all flex justify-center items-center gap-2 disabled:opacity-70"
					>
						{loading ? (
							<>
								<Loader2 className="animate-spin" size={20} /> Processando...
							</>
						) : (
							"Confirmar Proposta Digitalmente"
						)}
					</button>
				</form>
			</div>
		</div>
	);
};

export const RejectModal = ({
	isOpen,
	onClose,
	onReject,
	rejectData,
	setRejectData,
	rejectErrors,
	setRejectErrors,
	loading,
	rejectionReasons = [],
}) => {
	if (!isOpen) return null;
	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
			<button
				type="button"
				className="absolute inset-0 bg-black/80 backdrop-blur-sm w-full h-full border-none"
				onClick={onClose}
				aria-label="Fechar modal"
			></button>
			<div className="relative bg-[#181C25] border border-slate-700 rounded-2xl p-8 max-w-lg w-full shadow-2xl">
				<button
					type="button"
					onClick={onClose}
					className="absolute top-4 right-4 text-slate-500 hover:text-white"
				>
					<X size={20} />
				</button>
				<div className="mb-6">
					<h3 className="text-2xl font-bold text-white">
						Nao Aceitar Proposta
					</h3>
					<p className="text-slate-400 text-sm mt-1">
						Nos ajude a entender o motivo.
					</p>
				</div>
				{rejectErrors.general && (
					<div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
						{rejectErrors.general}
					</div>
				)}
				<div className="space-y-4">
					<div>
						<label
							htmlFor="rejection_reason"
							className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider"
						>
							Motivo *
						</label>
						<select
							id="rejection_reason"
							value={rejectData.rejection_reason}
							onChange={(e) => {
								setRejectData({
									...rejectData,
									rejection_reason: e.target.value,
								});
								setRejectErrors({});
							}}
							className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
						>
							<option value="">Selecione um motivo</option>
							{rejectionReasons.length > 0 ? (
								rejectionReasons.map((r) => (
									<option key={r.id} value={r.label}>
										{r.label}
									</option>
								))
							) : (
								<>
									<option value="Fora do orcamento">Fora do orcamento</option>
									<option value="Servico nao necessario">
										Servico nao necessario
									</option>
									<option value="Encontramos melhor proposta">
										Encontramos melhor proposta
									</option>
									<option value="Prazo nao adequado">Prazo nao adequado</option>
									<option value="Outro">Outro</option>
								</>
							)}
						</select>
						{rejectErrors.rejection_reason && (
							<p className="text-xs text-red-400 mt-1">
								{rejectErrors.rejection_reason}
							</p>
						)}
					</div>
					<div>
						<label
							htmlFor="rejection_observations"
							className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider"
						>
							Observacoes (Opcional)
						</label>
						<textarea
							id="rejection_observations"
							rows="3"
							placeholder="Detalhe os motivos..."
							value={rejectData.rejection_observations}
							onChange={(e) =>
								setRejectData({
									...rejectData,
									rejection_observations: e.target.value,
								})
							}
							className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 resize-none"
						/>
					</div>
					<button
						type="button"
						onClick={onReject}
						disabled={loading}
						className="w-full bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 font-bold py-4 rounded-xl transition-all flex justify-center items-center gap-2 disabled:opacity-70"
					>
						{loading ? (
							<>
								<Loader2 className="animate-spin" size={20} /> Processando...
							</>
						) : (
							"Confirmar Rejeicao"
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

AcceptModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onAccept: PropTypes.func.isRequired,
	formData: PropTypes.shape({
		approver_name: PropTypes.string,
		approver_role: PropTypes.string,
		approver_phone: PropTypes.string,
		approver_email: PropTypes.string,
	}).isRequired,
	setFormData: PropTypes.func.isRequired,
	formErrors: PropTypes.object.isRequired,
	setFormErrors: PropTypes.func.isRequired,
	loading: PropTypes.bool.isRequired,
	proposalNumber: PropTypes.string.isRequired,
	totalValue: PropTypes.string.isRequired,
	acceptanceConfig: PropTypes.shape({
		require_role: PropTypes.bool,
		require_phone: PropTypes.bool,
		legal_text: PropTypes.string,
	}).isRequired,
};

RejectModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
	rejectData: PropTypes.shape({
		rejection_reason: PropTypes.string,
		rejection_observations: PropTypes.string,
	}).isRequired,
	setRejectData: PropTypes.func.isRequired,
	rejectErrors: PropTypes.object.isRequired,
	setRejectErrors: PropTypes.func.isRequired,
	loading: PropTypes.bool.isRequired,
	rejectionReasons: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			label: PropTypes.string,
		}),
	),
};
